import { useRoute } from '@react-navigation/native';

/**
 * Routes that support "All Locations" mode for aggregate queries.
 * These are main backoffice pages where viewing data across all locations makes sense.
 */
const ALL_LOCATIONS_ELIGIBLE_ROUTES = [
  'Dashboard',
  'BackOfficeEmployees',
  'Inventory',
  'Sales',
  'Expenses',
  'Orders',
  'BackOfficeReports',
  'BackOfficeSummary',
] as const;

type AllLocationsEligibleRoute = (typeof ALL_LOCATIONS_ELIGIBLE_ROUTES)[number];

/**
 * Hook to determine if the current route is eligible for "All Locations" mode.
 * Only main backoffice pages should show the "All Locations" option.
 */
export const useAllLocationsRoute = () => {
  const route = useRoute();
  const routeName = route.name as string;
  const isEligible = ALL_LOCATIONS_ELIGIBLE_ROUTES.includes(
    routeName as AllLocationsEligibleRoute,
  );

  return {
    isEligible,
    routeName,
  };
};

/**
 * Check if a route name is eligible for "All Locations" mode.
 * Useful when you don't have access to the hook (e.g., in navigation options).
 */
export const isAllLocationsEligibleRoute = (
  routeName: string,
  locations: any[],
): boolean => {
  return (
    ALL_LOCATIONS_ELIGIBLE_ROUTES.includes(
      routeName as AllLocationsEligibleRoute,
    ) && locations.length > 1
  );
};

export { ALL_LOCATIONS_ELIGIBLE_ROUTES };
